import { useState } from "react";
import { Reorder } from "framer-motion";
import { GripVertical, Plus, Trash2 } from "lucide-react";

const REF_TYPES = ["book", "film", "tv", "youtube", "essay"];
let COUNTER = 0;
const uid = () => `k${Date.now()}_${COUNTER++}`;
const emptyRef = () => ({ ref_type: "book", title: "", creator: "", note: "", link: "" });

const withKeys = (modules) =>
  (modules || []).map((m) => ({
    _k: uid(),
    title: m.title || "",
    note: m.note || "",
    steps: (m.steps || []).map((s) => ({
      _k: uid(),
      core: { ...emptyRef(), ...(s.core || {}) },
      companion: { ...emptyRef(), ref_type: "youtube", ...(s.companion || {}) },
      optional: { ...emptyRef(), ...(s.optional || {}) },
    })),
  }));

const inputCls = "w-full bg-transparent border border-line px-3 py-2 text-vellum font-sans text-sm focus:border-crimson focus:outline-none placeholder:text-vellum-faint";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-faint mb-1 block";

function RefEditor({ value, onChange, testPrefix }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className={labelCls}>Type</label>
        <select value={value.ref_type} onChange={(e) => set("ref_type", e.target.value)} className={inputCls} data-testid={`${testPrefix}-type`}>
          {REF_TYPES.map((t) => <option key={t} value={t} className="bg-ink">{t}</option>)}
        </select>
      </div>
      <div><label className={labelCls}>Creator</label><input value={value.creator} onChange={(e) => set("creator", e.target.value)} className={inputCls} /></div>
      <div className="col-span-2"><label className={labelCls}>Title</label><input value={value.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid={`${testPrefix}-title`} /></div>
      <div className="col-span-2"><label className={labelCls}>Note</label><input value={value.note} onChange={(e) => set("note", e.target.value)} className={inputCls} /></div>
      <div className="col-span-2"><label className={labelCls}>Link</label><input value={value.link} onChange={(e) => set("link", e.target.value)} className={inputCls} placeholder="https://…" /></div>
    </div>
  );
}

const cleanRef = (r) => (r && r.title && r.title.trim() ? { ref_type: r.ref_type, title: r.title, creator: r.creator, note: r.note, link: r.link } : null);

export default function ModulesEditor({ initial, onChange }) {
  const [modules, setModules] = useState(() => withKeys(initial));

  const commit = (next) => {
    setModules(next);
    onChange(next.map((m) => ({
      title: m.title,
      note: m.note,
      steps: m.steps.map((s) => ({
        core: { ref_type: s.core.ref_type, title: s.core.title, creator: s.core.creator, note: s.core.note, link: s.core.link },
        companion: cleanRef(s.companion),
        optional: cleanRef(s.optional),
      })),
    })));
  };

  const updateModule = (mk, patch) => commit(modules.map((m) => (m._k === mk ? { ...m, ...patch } : m)));
  const addModule = () => commit([...modules, { _k: uid(), title: "", note: "", steps: [] }]);
  const delModule = (mk) => commit(modules.filter((m) => m._k !== mk));
  const setSteps = (mk, steps) => commit(modules.map((m) => (m._k === mk ? { ...m, steps } : m)));
  const addStep = (m) => setSteps(m._k, [...m.steps, { _k: uid(), core: emptyRef(), companion: { ...emptyRef(), ref_type: "youtube" }, optional: emptyRef() }]);
  const delStep = (m, sk) => setSteps(m._k, m.steps.filter((s) => s._k !== sk));
  const updateStep = (m, sk, patch) => setSteps(m._k, m.steps.map((s) => (s._k === sk ? { ...s, ...patch } : s)));

  return (
    <div data-testid="modules-editor">
      <div className="flex items-center justify-between mb-4">
        <p className="overline text-brass">Modules & Steps</p>
        <button onClick={addModule} data-testid="add-module" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 border border-line text-vellum hover:border-crimson hover:text-crimson transition-colors"><Plus className="w-3.5 h-3.5" /> Module</button>
      </div>

      <Reorder.Group axis="y" values={modules} onReorder={commit} className="space-y-4">
        {modules.map((m, mi) => (
          <Reorder.Item key={m._k} value={m} className="border border-line bg-ink p-4" data-testid={`module-${mi}`}>
            <div className="flex items-start gap-3 mb-3">
              <GripVertical className="w-5 h-5 text-vellum-faint mt-2 cursor-grab active:cursor-grabbing shrink-0" />
              <div className="flex-1 grid grid-cols-1 gap-2">
                <input value={m.title} onChange={(e) => updateModule(m._k, { title: e.target.value })} placeholder={`Module ${mi + 1} title`} className={`${inputCls} font-serif text-lg`} data-testid={`module-title-${mi}`} />
                <input value={m.note} onChange={(e) => updateModule(m._k, { note: e.target.value })} placeholder="Module note (optional)" className={inputCls} />
              </div>
              <button onClick={() => delModule(m._k)} aria-label="Delete module" className="text-vellum-muted hover:text-crimson mt-2"><Trash2 className="w-4 h-4" /></button>
            </div>

            <Reorder.Group axis="y" values={m.steps} onReorder={(steps) => setSteps(m._k, steps)} className="space-y-3 pl-8">
              {m.steps.map((s, si) => (
                <Reorder.Item key={s._k} value={s} className="border border-line bg-ink-800 p-4" data-testid={`step-editor-${mi}-${si}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <GripVertical className="w-4 h-4 text-vellum-faint cursor-grab active:cursor-grabbing" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-faint">Step {si + 1}</span>
                    <button onClick={() => delStep(m, s._k)} aria-label="Delete step" className="ml-auto text-vellum-muted hover:text-crimson"><Trash2 className="w-4 h-4" /></button>
                  </div>

                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-crimson mb-2">Core · primary text (required)</p>
                  <RefEditor value={s.core} onChange={(core) => updateStep(m, s._k, { core })} testPrefix={`core-${mi}-${si}`} />

                  <div className="mt-4 pl-4 border-l border-line">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brass mb-2">Companion · use alongside (optional)</p>
                    <RefEditor value={s.companion} onChange={(companion) => updateStep(m, s._k, { companion })} testPrefix={`comp-${mi}-${si}`} />
                  </div>

                  <div className="mt-4 pl-4 border-l border-line">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-faint mb-2">Optional · excellent but not necessary</p>
                    <RefEditor value={s.optional} onChange={(optional) => updateStep(m, s._k, { optional })} testPrefix={`opt-${mi}-${si}`} />
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>

            <button onClick={() => addStep(m)} data-testid={`add-step-${mi}`} className="mt-3 ml-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 border border-line text-vellum hover:border-crimson hover:text-crimson transition-colors"><Plus className="w-3.5 h-3.5" /> Step</button>
          </Reorder.Item>
        ))}
      </Reorder.Group>
    </div>
  );
}
